import type { CSSProperties } from "react";
import type { ReactNode } from "react";

import { useHover } from "@react-aria/interactions";

import type { StrictColor } from "@similarweb/styles";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import HighlightTerm from "app/components/highlight-term/HighlightTerm";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { minVisitsAbbrFilter } from "app/filters/ngFilters";

import { Checkbox } from "./components/Checkbox";
import type { Keyword } from "./types";

const formatter = (value: number) => {
    return minVisitsAbbrFilter()(value, 0);
};

const Tag = ({
    backgroundColor = colorsPalettes.blue[100],
    color = colorsPalettes.blue[400],
    children,
}: {
    backgroundColor?: string;
    color?: StrictColor;
    children: ReactNode;
}) => {
    return (
        <Text
            fontVariant="tag"
            paddingX="8px"
            paddingY="4px"
            color={color}
            background={backgroundColor}
            borderRadius="16px"
            style={{ letterSpacing: "0.6px" }}
        >
            {children}
        </Text>
    );
};

const Spacer = ({ width }: { width: string }) => <Box width={width} />;

export const KeywordListComposerKeyword = ({
    style,
    keyword,
    query = "",
    onSelectKeyword,
    onDeleteKeyword,
}: {
    style: CSSProperties;
    keyword: Keyword;
    query?: string;
    onSelectKeyword?: (keyword: Keyword) => void;
    onDeleteKeyword?: (keyword: Keyword) => void;
}) => {
    const t = useTranslation();
    const { hoverProps, isHovered } = useHover({});
    const isSelectable = onSelectKeyword !== undefined;
    const isDeletable = onDeleteKeyword !== undefined;

    return (
        <Flex
            data-automation="keyword-list-composer-keyword-item"
            style={style}
            justifyContent="space-between"
            alignItems="center"
            whenHover={{ background: colorsPalettes.blue[50] }}
            {...hoverProps}
        >
            <Flex
                alignItems="center"
                flexGrow={1}
                cursor={isSelectable ? "pointer" : "default"}
                onClick={() => {
                    if (isSelectable) {
                        onSelectKeyword(keyword);
                    }
                }}
            >
                {(isHovered && isSelectable) || keyword.selected ? (
                    <Flex
                        width="48px"
                        justifyContent="center"
                    >
                        <Checkbox selected={keyword.selected} />
                    </Flex>
                ) : (
                    <Spacer width="48px" />
                )}
                <Flex gap="8px">
                    <Text
                        fontVariant="input2"
                        color={colorsPalettes.carbon[400]}
                    >
                        <HighlightTerm
                            sentence={keyword.term}
                            highlightTerm={query}
                        />
                    </Text>
                    {keyword.isSeed ? <Tag>{t("auto.keyword.list.generator.body.keywords.seed.indicator")}</Tag> : null}
                    {keyword.isRecentlyAdded && !keyword.isSeed ? (
                        <Tag
                            color={colorsPalettes.carbon[0]}
                            backgroundColor={colorsPalettes.carbon[300]}
                        >
                            {t("auto.keyword.list.generator.body.keywords.recently.indicator")}
                        </Tag>
                    ) : null}
                </Flex>
            </Flex>
            <Flex alignItems="center">
                {keyword.isLoading ? (
                    <PixelPlaceholderLoader
                        width={"60px"}
                        height={"20px"}
                    />
                ) : (
                    <Text
                        fontVariant="input2"
                        color={colorsPalettes.carbon[400]}
                    >
                        {keyword.volume ? formatter(keyword.volume) : t("common.n/a")}
                    </Text>
                )}
                {isHovered && isDeletable ? (
                    <Flex
                        width="48px"
                        justifyContent="center"
                    >
                        <IconButton
                            type="flat"
                            iconSize="xs"
                            iconName="delete"
                            onClick={() => onDeleteKeyword(keyword)}
                        />
                    </Flex>
                ) : (
                    <Spacer width="48px" />
                )}
            </Flex>
        </Flex>
    );
};

KeywordListComposerKeyword.height = 44;
